import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FlaskConical, Loader2, Mail, Lock, User, Hash } from 'lucide-react';
import { api } from '@/services';
import { useApp } from '@/context/AppContext';
import { azureAuthService } from '@/services/azureAuthService';
import Watermark from '@/components/ui/Watermark';
import CopyrightFooter from '@/components/ui/CopyrightFooter';

export default function SignupPage() {
  const navigate = useNavigate();
  const { loginWithGoogle, loginWithMicrosoft } = useApp();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [studentId, setStudentId] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError('');

    if (!firstName.trim() || !lastName.trim() || !email.trim() || !password) {
      setError('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const result = await api.post<{ success: boolean; message?: string; error?: string }>(
        '/auth/register',
        { email, password, firstName, lastName, studentId: studentId.trim() || undefined }
      );

      if (result.success && result.data?.success) {
        setSuccess(true);
      } else {
        setError(result.data?.error || result.error || 'Registration failed');
      }
    } catch (err) {
      setError('Registration failed. Please try again.');
    }
    setLoading(false);
  };

  const handleGoogleSignup = () => {
    loginWithGoogle(); // OAuth is inherently sign-up-or-sign-in
  };

  const handleMicrosoftSignup = async () => {
    const result = await loginWithMicrosoft();
    if (result.success) {
      navigate('/courses');
    } else {
      setError(result.error || 'Microsoft sign up failed');
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-card">
        <div className="signup-header">
          <div className="signup-icon">
            <FlaskConical size={32} />
          </div>
          <h1>BDP vLab</h1>
          <p>Create an Account</p>
        </div>

        {success ? (
          <div className="signup-success">
            <div className="success-icon">&#10003;</div>
            <h2>Account Created!</h2>
            <p>Your instructor account has been created successfully. You can now sign in.</p>
            <button className="signup-submit instructor" onClick={() => navigate('/login')}>
              Go to Sign In
            </button>
          </div>
        ) : (
          <>
            <div className="signup-info">
              <p>Create an account to access virtual laboratory simulations. You can also sign up with Google or Microsoft.</p>
            </div>

            <form onSubmit={handleSubmit} className="signup-form">
              <div className="input-row">
                <div className="input-group">
                  <User size={16} className="input-icon" />
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First Name"
                    className="signup-input"
                    disabled={loading}
                  />
                </div>
                <div className="input-group">
                  <User size={16} className="input-icon" />
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last Name"
                    className="signup-input"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="input-group">
                <Hash size={16} className="input-icon" />
                <input
                  type="text"
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value)}
                  placeholder="Student ID (optional, e.g. 100111222)"
                  className="signup-input"
                  disabled={loading}
                />
              </div>

              <div className="input-group">
                <Mail size={16} className="input-icon" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email Address"
                  className="signup-input"
                  disabled={loading}
                />
              </div>

              <div className="input-group">
                <Lock size={16} className="input-icon" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password (min 6 characters)"
                  className="signup-input"
                  disabled={loading}
                />
              </div>

              <div className="input-group">
                <Lock size={16} className="input-icon" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm Password"
                  className="signup-input"
                  disabled={loading}
                />
              </div>

              {error && <p className="signup-error">{error}</p>}

              <button type="submit" className="signup-submit instructor" disabled={loading}>
                {loading ? <Loader2 size={18} className="spin" /> : 'Create Account'}
              </button>
            </form>

            <div className="signup-divider">
              <span>or sign up with</span>
            </div>

            <div className="oauth-buttons">
              <button
                type="button"
                className="oauth-btn google"
                onClick={handleGoogleSignup}
                disabled={loading}
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign up with Google
              </button>

              {azureAuthService.isConfigured() && (
                <button
                  type="button"
                  className="oauth-btn microsoft"
                  onClick={handleMicrosoftSignup}
                  disabled={loading}
                >
                  <svg width="18" height="18" viewBox="0 0 21 21">
                    <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                    <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                    <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                    <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                  </svg>
                  Sign up with Microsoft
                </button>
              )}
            </div>

            <div className="signup-footer">
              <p>Already have an account? <Link to="/login" className="signup-link">Sign In</Link></p>
              <p><Link to="/" className="signup-link-secondary">Back to Home</Link></p>
            </div>
          </>
        )}
      </div>

      <style>{`
        .signup-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0F0F13 0%, #1A1A24 50%, #13131A 100%);
          padding: 40px 20px;
        }
        .signup-card {
          background: rgba(255,255,255,0.04);
          border-radius: 20px;
          padding: 44px;
          width: 480px;
          max-width: 100%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
          border: 1px solid rgba(255,255,255,0.08);
        }
        .signup-header {
          text-align: center;
          margin-bottom: 28px;
        }
        .signup-icon {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #FF5A36, #00A86B);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          color: white;
        }
        .signup-header h1 {
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 4px;
          color: #FFFFFF;
        }
        .signup-header p {
          color: rgba(255,255,255,0.5);
          font-size: 14px;
          margin: 0;
        }
        .signup-info {
          background: rgba(0,168,107,0.06);
          border: 1px solid rgba(0,168,107,0.15);
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 24px;
        }
        .signup-info p {
          font-size: 13px;
          line-height: 1.6;
          color: rgba(255,255,255,0.6);
          margin: 0;
        }
        .signup-note {
          margin-top: 8px !important;
          font-style: italic;
          color: rgba(255,255,255,0.4) !important;
        }
        .signup-form {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .input-row {
          display: flex;
          gap: 12px;
        }
        .input-row .input-group {
          flex: 1;
        }
        .input-group {
          position: relative;
        }
        .input-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(255,255,255,0.3);
          pointer-events: none;
        }
        .signup-input {
          width: 100%;
          padding: 13px 16px 13px 40px;
          border: 2px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          background: rgba(255,255,255,0.04);
          color: #FFFFFF;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s;
          box-sizing: border-box;
        }
        .signup-input:focus {
          border-color: rgba(0,168,107,0.5);
        }
        .signup-input::placeholder {
          color: rgba(255,255,255,0.3);
        }
        .signup-input:disabled {
          opacity: 0.6;
        }
        .signup-error {
          color: #EF4444;
          font-size: 13px;
          text-align: center;
          margin: 0;
        }
        .signup-submit {
          width: 100%;
          padding: 14px;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          font-size: 15px;
          color: white;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 4px;
        }
        .signup-submit.instructor {
          background: linear-gradient(135deg, #00A86B, #2E8B57);
        }
        .signup-submit:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0,168,107,0.3);
        }
        .signup-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        .signup-divider {
          display: flex;
          align-items: center;
          gap: 16px;
          margin: 20px 0;
        }
        .signup-divider::before,
        .signup-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: rgba(255,255,255,0.1);
        }
        .signup-divider span {
          color: rgba(255,255,255,0.4);
          font-size: 13px;
          white-space: nowrap;
        }
        .oauth-buttons {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .oauth-btn {
          width: 100%;
          padding: 12px;
          border: 2px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: background 0.2s, border-color 0.2s;
        }
        .oauth-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .oauth-btn.google {
          background: #fff;
          color: #333;
          border-color: #ddd;
        }
        .oauth-btn.google:hover:not(:disabled) {
          background: #f5f5f5;
          border-color: #bbb;
        }
        .oauth-btn.microsoft {
          background: rgba(255,255,255,0.06);
          color: #FFFFFF;
        }
        .oauth-btn.microsoft:hover:not(:disabled) {
          background: rgba(255,255,255,0.1);
          border-color: rgba(255,255,255,0.2);
        }
        .signup-footer {
          text-align: center;
          margin-top: 24px;
        }
        .signup-footer p {
          font-size: 13px;
          color: rgba(255,255,255,0.4);
          margin: 6px 0;
        }
        .signup-link {
          color: #FF7B5C;
          text-decoration: none;
          font-weight: 600;
        }
        .signup-link:hover {
          text-decoration: underline;
        }
        .signup-link-secondary {
          color: rgba(255,255,255,0.4);
          text-decoration: none;
        }
        .signup-link-secondary:hover {
          color: rgba(255,255,255,0.6);
        }
        .signup-success {
          text-align: center;
          padding: 20px 0;
        }
        .success-icon {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #00A86B, #2E8B57);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          color: white;
          margin: 0 auto 20px;
        }
        .signup-success h2 {
          font-size: 22px;
          font-weight: 700;
          margin: 0 0 8px;
          color: #FFFFFF;
        }
        .signup-success p {
          font-size: 14px;
          color: rgba(255,255,255,0.5);
          margin: 0 0 24px;
          line-height: 1.6;
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 520px) {
          .signup-card {
            padding: 32px 24px;
          }
          .input-row {
            flex-direction: column;
            gap: 14px;
          }
        }
      `}</style>
      <Watermark />
      <CopyrightFooter />
    </div>
  );
}
